import { Injectable, computed, signal } from "@angular/core";
import { Event, NavigationEnd, Router } from "@angular/router";
import { environment } from "src/environments/environment";
import { NavItem } from "src/app/layouts/full/vertical/sidebar/nav-item/nav-item";
import { navItems as defaultNavItems } from "src/app/layouts/full/vertical/sidebar/sidebar-data";
import { StoreGetDTO } from "src/app/pages/store-management/stores/store";
import { ProviderGetDTO } from "src/app/pages/providers/provider.model";

@Injectable({ providedIn: "root" })
export class NavService {
  showClass: any = false;

  public currentUrl = signal<string | undefined>(undefined);
  public sidebarCollapsed = signal<boolean>(false);
  public navItems = signal<NavItem[]>(defaultNavItems);

  private readonly expandedNavItemIds = signal<ReadonlySet<string>>(new Set());
  readonly collapseAllNavDropdownsTrigger = signal(0);
  readonly hasExpandedNavDropdowns = computed(
    () => this.expandedNavItemIds().size > 0,
  );

  setNavDropdownExpanded(trackId: string, expanded: boolean): void {
    if (!trackId) {
      return;
    }

    this.expandedNavItemIds.update((current) => {
      const next = new Set(current);
      if (expanded) {
        next.add(trackId);
      } else {
        next.delete(trackId);
      }
      return next;
    });
  }

  collapseAllNavDropdowns(): void {
    this.expandedNavItemIds.set(new Set());
    this.collapseAllNavDropdownsTrigger.update((value) => value + 1);
  }

  constructor(private router: Router) {
    this.router.events.subscribe((event: Event) => {
      if (event instanceof NavigationEnd) {
        this.currentUrl.set(event.urlAfterRedirects);
      }
    });
  }

  /**
   * Update nav items with dynamic content (e.g., dynamic claims)
   */
  updateNavItems(items: NavItem[]): void {
    this.navItems.set(items);
  }

  buildNavItemsWithDynamicClaims(
    baseNavItems: NavItem[],
    stores: StoreGetDTO[],
    providers: ProviderGetDTO[],
    isOnboardingIncomplete: boolean = false,
  ): {
    navItems: NavItem[];
    unblocked: NavItem[];
    blocked: NavItem[];
  } {
    const navItemsCopy = this.cloneNavItems(baseNavItems);
    const hasStores = !!stores?.length;
    const hasCompleteStore = stores?.some(
      (store) => store.onboarding?.isComplete === true,
    );
    const shouldBlockAll =
      isOnboardingIncomplete || !hasStores || !hasCompleteStore;

    if (hasStores) {
      const storesByProvider = new Map<string, StoreGetDTO[]>();

      stores.forEach((store) => {
        const provider = store.provider;
        if (!storesByProvider.has(provider)) {
          storesByProvider.set(provider, []);
        }
        storesByProvider.get(provider)!.push(store);
      });

      const providerIds = Array.from(storesByProvider.keys());
      const validProviderIds = providerIds.filter((providerId) =>
        storesByProvider
          .get(providerId)
          ?.some((store) => store.onboarding?.isComplete === true),
      );
      const invalidProviderIds = providerIds.filter(
        (providerId) =>
          !storesByProvider
            .get(providerId)
            ?.some((store) => store.onboarding?.isComplete === true),
      );

      const validClaimsNavItem = this.buildClaimsNavItem(
        validProviderIds,
        providers,
        false,
      );
      const invalidClaimsNavItem = this.buildClaimsNavItem(
        invalidProviderIds,
        providers,
        true,
      );

      const managementIndex = navItemsCopy.findIndex(
        (item) => item.displayName === "Management",
      );

      if (managementIndex !== -1) {
        if (validClaimsNavItem) {
          navItemsCopy.splice(managementIndex + 1, 0, validClaimsNavItem);
        }

        if (invalidClaimsNavItem) {
          const insertIndex =
            managementIndex + 1 + (validClaimsNavItem ? 1 : 0);
          navItemsCopy.splice(insertIndex, 0, invalidClaimsNavItem);
        }
      }
    }

    const processedNavItems = this.applyStoreBlocking(
      navItemsCopy,
      shouldBlockAll,
    );

    if (hasStores) {
      this.markEmptyNavCaps(processedNavItems);
    }

    const groups = this.splitNavItems(processedNavItems);

    return {
      navItems: processedNavItems,
      unblocked: groups.unblocked,
      blocked: groups.blocked,
    };
  }

  splitNavItems(items: NavItem[]): {
    unblocked: NavItem[];
    blocked: NavItem[];
  } {
    const unblocked: NavItem[] = [];
    const blocked: NavItem[] = [];
    let pendingNavCap: NavItem | null = null;
    let pendingSection: NavItem[] = [];

    const commitSection = () => {
      if (!pendingNavCap) {
        return;
      }
      if (pendingSection.length) {
        unblocked.push(pendingNavCap, ...pendingSection);
      }
      pendingNavCap = null;
      pendingSection = [];
    };

    for (const item of items) {
      if (item.navCap) {
        commitSection();
        pendingNavCap = item;
        continue;
      }

      if (item.children && item.children.length) {
        const childGroups = this.splitNavItems(item.children);

        if (item.block) {
          const blockedChildren = childGroups.blocked;
          const unblockedChildren = childGroups.unblocked;
          if (item.route || item.external) {
            blocked.push({
              ...item,
              children: blockedChildren.length ? blockedChildren : undefined,
            });
          } else if (blockedChildren.length || unblockedChildren.length) {
            blocked.push({
              ...item,
              block: false,
              children: blockedChildren.length
                ? blockedChildren
                : item.children,
            });
          }
          continue;
        }

        const unblockedChildren = childGroups.unblocked;
        const blockedChildren = childGroups.blocked;
        if (unblockedChildren.length) {
          const cloned: NavItem = {
            ...item,
            children: unblockedChildren,
          };

          if (pendingNavCap) {
            pendingSection.push(cloned);
          } else {
            unblocked.push(cloned);
          }
        }

        if (blockedChildren.length) {
          blocked.push({
            ...item,
            block: false,
            children: blockedChildren,
          });
        }

        continue;
      }

      if (item.block) {
        blocked.push(item);
        continue;
      }

      if (pendingNavCap) {
        pendingSection.push(item);
      } else {
        unblocked.push(item);
      }
    }

    commitSection();
    return { unblocked, blocked };
  }

  private buildClaimsNavItem(
    providerIds: string[],
    providers: ProviderGetDTO[],
    blocked: boolean,
  ): NavItem | null {
    if (!providerIds.length) {
      return null;
    }

    if (providerIds.length === 1) {
      const providerId = providerIds[0];
      const claimsConfig = environment.claimsTypesNavItems.find(
        (item) => item.provider === providerId,
      );
      const claimTypes = (claimsConfig?.children || []).map((claimType) => ({
        ...claimType,
        block: blocked,
      }));

      return {
        displayName: "Claims",
        iconName: "clipboard-list",
        children: claimTypes,
        block: blocked,
      };
    }

    const providerChildren: NavItem[] = providerIds.map((providerId) => {
      const providerData = providers.find((p) => p.value === providerId);
      const claimsConfig = environment.claimsTypesNavItems.find(
        (item) => item.provider === providerId,
      );
      const claimTypes = (claimsConfig?.children || []).map((claimType) => ({
        ...claimType,
        block: blocked,
      }));

      return {
        displayName: providerData?.name || providerId,
        iconName: "folder",
        children: claimTypes,
      };
    });

    return {
      displayName: "Claims",
      iconName: "clipboard-list",
      children: providerChildren,
      block: blocked,
    };
  }

  private markEmptyNavCaps(items: NavItem[]): void {
    let currentNavCap: NavItem | null = null;
    let hasUnblockedUnderCap = false;

    const commitNavCap = () => {
      if (currentNavCap && !hasUnblockedUnderCap) {
        currentNavCap.block = true;
      }
      hasUnblockedUnderCap = false;
    };

    for (const item of items) {
      if (item.navCap) {
        commitNavCap();
        currentNavCap = item;
      } else if (this.hasUnblockedDescendant(item)) {
        hasUnblockedUnderCap = true;
      }
    }
    commitNavCap();
  }

  private hasUnblockedDescendant(item: NavItem): boolean {
    if (item.block !== true) {
      return true;
    }
    return !!item.children?.some((child) => this.hasUnblockedDescendant(child));
  }

  private cloneNavItems(items: NavItem[]): NavItem[] {
    return items.map((item) => ({
      ...item,
      children: item.children ? this.cloneNavItems(item.children) : undefined,
    }));
  }

  private applyStoreBlocking(
    items: NavItem[],
    shouldBlockAll: boolean,
  ): NavItem[] {
    return items.map((item) => {
      const cloned: NavItem = {
        ...item,
        children: item.children
          ? this.applyStoreBlocking(item.children, shouldBlockAll)
          : undefined,
      };

      if (!shouldBlockAll) {
        return cloned;
      }

      const isDashboard = cloned.route === "/dashboard";
      const isStore = cloned.route === "/store/stores";
      const isFaq = cloned.route === "/help/faqs";
      const isKnowledgeBase =
        cloned.route?.startsWith("/help/knowledge-base") ?? false;
      const isLegal =
        cloned.route?.startsWith("/legal") ||
        cloned.displayName === "Policies & Agreements" ||
        cloned.navCap === "Legal";
      const isHelpCenter =
        cloned.displayName === "helpCenter" ||
        cloned.displayName === "Help Center";
      const isNavCap = !!cloned.navCap;
      const hasUnblockedChild = cloned.children?.some(
        (child) => child.block === false,
      );

      if (isNavCap) {
        cloned.block = false;
      } else if (
        isDashboard ||
        isStore ||
        isFaq ||
        isKnowledgeBase ||
        isHelpCenter ||
        isLegal ||
        hasUnblockedChild
      ) {
        cloned.block = false;
      } else {
        cloned.block = true;
      }

      return cloned;
    });
  }
}
